/**
 * SourcingPanel — the shared body of the sourcing surface, rendered by both the
 * desktop route and the mobile screen so they never drift. Branches on `live`:
 * inert ⇒ the pitch + "coming soon" (never a button that errors); live ⇒ the
 * place form + the open-orders book with inline confirm.
 */
import { useState } from 'react'
import { useSourcing } from '../../lib/useSourcing'
import {
  SOURCING_PLAN, formatSourcingFee, formatGuarantee, sourcingStatusLabel,
  isConfirmable, isWorkingOrder, type SourcingOrderView,
} from '../../lib/sourcing'
import { SourcingForm } from './SourcingForm'
import { SourcingConfirm } from './SourcingConfirm'

/** A short human summary of a saved spec ("2ct Round · D–F · VVS"). */
function specSummary(o: SourcingOrderView): string {
  const s = o.spec as Record<string, unknown>
  const parts: string[] = []
  const shape = Array.isArray(s.shape) ? (s.shape as string[]) : []
  if (shape.length) parts.push(shape.join('/'))
  const cMin = s.caratMin as number | undefined, cMax = s.caratMax as number | undefined
  if (cMin != null || cMax != null) parts.push(`${cMin ?? '0'}–${cMax ?? '∞'}ct`)
  const color = Array.isArray(s.color) ? (s.color as string[]) : []
  if (color.length) parts.push(color.join(''))
  const clarity = Array.isArray(s.clarity) ? (s.clarity as string[]) : []
  if (clarity.length) parts.push(clarity.join('/'))
  return parts.length ? parts.join(' · ') : 'Any diamond'
}

const money = (n: number) => `$${(Number.isFinite(n) ? n : 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`

function StatusPill({ status }: { status: SourcingOrderView['status'] }) {
  const tone =
    status === 'matched' ? 'bg-gold/15 text-gold-deep'
    : status === 'purchased' ? 'bg-emerald-100 text-emerald-800'
    : status === 'cancelled' || status === 'expired' ? 'bg-navy/5 text-soft'
    : 'bg-blue-50 text-blue-700'
  return <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${tone}`}>{sourcingStatusLabel(status)}</span>
}

function OrderRow({ order, onCancel, onRefresh }: { order: SourcingOrderView; onCancel: (id: string) => void; onRefresh: () => void }) {
  const [confirming, setConfirming] = useState(false)
  const basisLabel = order.limitBasis === 'ppc' ? '/ct' : ' total'
  return (
    <li className="rounded-card border border-border bg-white p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-ink truncate">{specSummary(order)}</p>
          <p className="mt-0.5 text-xs text-soft">Ceiling {money(order.limitValue)}{basisLabel}
            {order.quantity > 1 ? ` · ${order.remainingQty}/${order.quantity} left` : ''}</p>
        </div>
        <StatusPill status={order.status} />
      </div>

      {isConfirmable(order) && order.match && !confirming && (
        <button onClick={() => setConfirming(true)}
          className="w-full px-4 py-2.5 rounded-full bg-gold text-white text-sm font-semibold hover:bg-gold-hover active:scale-[0.98] transition-all">
          Review match · {money(order.match.quotedTotal)}
        </button>
      )}
      {isConfirmable(order) && order.match && confirming && (
        <SourcingConfirm orderId={order.id} match={order.match}
          onDone={() => { setConfirming(false); onRefresh() }}
          onCancel={() => setConfirming(false)} />
      )}

      {isWorkingOrder(order.status) && (
        <div className="flex items-center gap-3 text-xs">
          <span className="text-soft">Searching our suppliers…</span>
          <button onClick={() => onCancel(order.id)} className="ml-auto font-semibold text-soft hover:text-red-600 transition-colors">Cancel</button>
        </div>
      )}
    </li>
  )
}

export function SourcingPanel() {
  const { live, loading, error, orders, member, place, cancel, refresh } = useSourcing()
  const [placing, setPlacing] = useState(false)
  const [placeError, setPlaceError] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)

  const openCount = orders.filter((o) => isWorkingOrder(o.status) || o.status === 'matched').length
  const maxOpenReached = openCount >= SOURCING_PLAN.maxOpenPerMember

  async function handlePlace(args: Parameters<typeof place>[0]) {
    setPlacing(true); setPlaceError(null)
    const res = await place(args)
    setPlacing(false)
    if (res.ok) setShowForm(false)
    else setPlaceError(res.error ?? 'Could not place your order.')
  }

  // ── Inert: the pitch, no working button (prod's current state) ─────────────
  if (!live) {
    return (
      <div className="rounded-card border border-border bg-paper p-6 space-y-4">
        <div className="space-y-2">
          <h2 className="font-serif text-xl font-bold text-ink">Can’t find it? We’ll source it.</h2>
          <p className="text-sm text-soft leading-relaxed">
            Tell us the spec and your ceiling price. We find the cheapest match across our suppliers and you
            keep the difference — sourcing is <span className="font-semibold text-ink">free</span>. Add optional
            return protection so a stone that doesn’t match grade gets replaced or repriced.
          </p>
        </div>
        <span className="inline-flex items-center rounded-full bg-navy/5 px-3 py-1 text-xs font-semibold text-soft">Coming soon</span>
      </div>
    )
  }

  // ── Live ───────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Fee headline — server-hydrated (invariant 4) */}
      <div className="rounded-card border border-gold/30 bg-gold/[0.04] p-4">
        <p className="text-sm text-ink">
          <span className="font-semibold">Sourcing is {formatSourcingFee(member ? SOURCING_PLAN.sourcingFeeMemberUsd : SOURCING_PLAN.sourcingFeeUsd)}.</span>{' '}
          You’re charged the cheapest match at or under your ceiling. Optional return protection is {formatGuarantee(member)}.
        </p>
        {!member && (
          <p className="mt-1.5 text-xs text-soft">
            Aska+ members get return protection at {formatGuarantee(true)} and access to Aska Credit.
          </p>
        )}
      </div>

      {/* Place a new order */}
      {showForm ? (
        <div className="rounded-card border border-border bg-white p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-serif text-lg font-bold text-ink">Source a diamond</h2>
            <button onClick={() => setShowForm(false)} className="text-sm font-semibold text-soft hover:text-ink">Close</button>
          </div>
          <SourcingForm placing={placing} onPlace={handlePlace} maxOpenReached={maxOpenReached} />
          {placeError && <p className="text-sm font-medium text-red-600">{placeError}</p>}
        </div>
      ) : (
        <button onClick={() => setShowForm(true)} disabled={maxOpenReached}
          className="w-full px-4 py-3 rounded-full bg-gold text-white font-semibold hover:bg-gold-hover active:scale-[0.98] transition-all disabled:opacity-50 disabled:active:scale-100">
          + Source a diamond
        </button>
      )}

      {/* The book */}
      <div className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-soft">Your sourcing orders</h2>
        {loading ? (
          <p className="text-sm text-soft">Loading…</p>
        ) : error ? (
          <p className="text-sm text-amber-700">{error}</p>
        ) : orders.length === 0 ? (
          <p className="text-sm text-soft">No sourcing orders yet. Place one above and we’ll start searching.</p>
        ) : (
          <ul className="space-y-3">
            {orders.map((o) => <OrderRow key={o.id} order={o} onCancel={cancel} onRefresh={refresh} />)}
          </ul>
        )}
      </div>
    </div>
  )
}
